import { useMemo } from 'react';

import { SIndexTableContext, SIndexTableSelectionChangeContext } from './context';
import { useSIndexTableBulkSelectionData, useSIndexTableHandleBulkSelection } from './hooks';
import type { SIndexTableProviderProps } from './types';

export function SIndexTableProvider({
    children,
    resourceName: passedResourceName,
    loading,
    onSelectionChange,
    selectedItemsCount = 0,
    itemCount,
    hasMoreItems,
    condensed,
    selectable: isSelectableIndex = true,
    paginatedSelectAllText: defaultPaginatedSelectAllText,
    disableSelection,
}: SIndexTableProviderProps) {
    const {
        paginatedSelectAllText,
        bulkActionsLabel,
        bulkActionsAccessibilityLabel,
        resourceName,
        selectMode,
        bulkSelectState,
    } = useSIndexTableBulkSelectionData({
        selectedItemsCount,
        itemCount,
        hasMoreItems,
        resourceName: passedResourceName,
        defaultPaginatedSelectAllText,
    });
    const handleSelectionChange = useSIndexTableHandleBulkSelection({ onSelectionChange });

    const contextValue = useMemo(
        () => ({
            itemCount,
            selectMode: selectMode && isSelectableIndex,
            selectable: isSelectableIndex,
            resourceName,
            loading,
            paginatedSelectAllText,
            hasMoreItems,
            bulkActionsLabel,
            bulkActionsAccessibilityLabel,
            bulkSelectState,
            selectedItemsCount,
            condensed,
            disableSelection,
        }),
        [
            itemCount,
            selectMode,
            isSelectableIndex,
            resourceName,
            loading,
            paginatedSelectAllText,
            hasMoreItems,
            bulkActionsLabel,
            bulkActionsAccessibilityLabel,
            bulkSelectState,
            selectedItemsCount,
            condensed,
        ]
    );

    return (
        <SIndexTableContext.Provider value={contextValue}>
            <SIndexTableSelectionChangeContext.Provider value={handleSelectionChange}>
                {children}
            </SIndexTableSelectionChangeContext.Provider>
        </SIndexTableContext.Provider>
    );
}
